import { Fragment, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Database, Search, RefreshCw, Loader2, X, ChevronLeft, ChevronRight,
  SlidersHorizontal, Plus, Upload, Pencil, Trash2, Tags, FileDown,
} from "lucide-react";

import {
  useGetDatasetItemsQuery,
  useGetDatasetFiltersQuery,
  useCreateDatasetEntryMutation,
  useUpdateDatasetEntryMutation,
  useDeleteDatasetEntryMutation,
  useImportDatasetMutation,
  useCreateDatasetCategoryMutation,
  useRenameDatasetCategoryMutation,
  useDeleteDatasetCategoryMutation,
  type CategoryLevel,
  type DatasetRow,
  type DatasetCategory,
  type DatasetValueSpec,
} from "../api/datasetApi";
import { useAuth } from "../../../context/AuthContexts";

/**
 * One screen for every per-type databank register.
 *
 * The register is the `:dataset` route segment, and everything that differs
 * between registers arrives in the API's `meta`: what the register is called,
 * what its number is called, what the described thing is called, how many
 * levels of category it files under, and whether it splits into variants.
 * Adding unit rates or plant hire later is a route entry and a menu link; this
 * file does not change.
 *
 * VARIANTS. Labour rates is formal and informal — one register, one table, but
 * three levels of filing on one side and one on the other, "Description" against
 * "Operative / Worker", "Rate" against "Daily Rate", and separate category
 * lists. The screen shows one variant at a time because they are genuinely
 * different things to read, and because every index on the table leads with the
 * variant column: a list that mixed them could not use them.
 *
 * Paging is by cursor, so there are no page numbers and no total on the pager.
 * That is not an omission: counting rows is the query that would make this
 * screen slow at the volumes the register is heading for, so the API does not
 * count and the UI does not pretend to know. `meta.total_rows` in the header is
 * pre-computed for the WHOLE register and is labelled as such.
 */

const EMPTY = {
  state_id: "", zone: "", category_id: "", sub_category_id: "", sub_category_2_id: "",
  unit_id: "", measurement_id: "", year: "", month: "", q: "",
};
type Filters = typeof EMPTY;

const emptyForm = {
  state_id: "", zone: "", description: "", unit: "", measurement: "", code: "",
  category_id: "", sub_category_id: "", sub_category_2_id: "",
  year: String(new Date().getFullYear()), month: "",
};

/** The register's numbers as form fields, keyed by column. */
type ValueDraft = Record<string, string>;

function extractError(err: any) {
  return (
    err?.data?.message ||
    err?.data?.errors?.[Object.keys(err?.data?.errors ?? {})[0]]?.[0] ||
    "Something went wrong"
  );
}

/** One CSV cell, quoted only when it has to be. */
function csvCell(value: string) {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Write lines to a file the browser downloads.
 *
 * CRLF, because these are opened in Excel on Windows; a BOM for the same
 * reason — without it Excel reads "₦" and any accented name as mojibake.
 */
function saveCsv(name: string, lines: string[]) {
  const blob = new Blob(["﻿" + lines.join("\r\n") + "\r\n"], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * `dataset` names the register directly, for the admin data-entry console,
 * which offers all of them on one page rather than one URL each. Left out —
 * the member's own /databank/register/:dataset route — it comes off the route
 * as before. `embedded` only drops the page wrapper, since the console
 * already provides one.
 */
type Props = { dataset?: string; embedded?: boolean };

export default function DatasetPage({ dataset: fixedDataset, embedded }: Props = {}) {
  const { dataset: routeDataset } = useParams();
  const dataset = fixedDataset ?? routeDataset ?? "composite_rates";
  const { user } = useAuth();

  const isAdmin = useMemo(() => {
    const names = ((user?.roles || []) as any[])
      .map((r) => (typeof r === "string" ? r : r?.name))
      .map((r) => String(r || "").toLowerCase());
    return names.some((r) => ["admin", "super_admin", "super admin"].includes(r));
  }, [user?.roles]);

  // Undefined until the API tells us which variants exist and which is default —
  // asking for one by name before that would be guessing at the register.
  const [variant, setVariant] = useState<string | undefined>(undefined);

  const [filters, setFilters] = useState<Filters>({ ...EMPTY });
  const [searchDraft, setSearchDraft] = useState("");
  // Cursor pagination has no page numbers, so "previous" is a stack of the
  // cursors already visited rather than an arithmetic step backwards.
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);

  const cursor = cursorStack[cursorStack.length - 1];

  const patch = (p: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...p }));
    setCursorStack([null]);
  };

  // Switching register (or variant) resets everything downstream — the other
  // half's categories and cursors mean nothing here.
  useEffect(() => {
    setFilters({ ...EMPTY });
    setSearchDraft("");
    setCursorStack([null]);
  }, [dataset, variant]);

  const { data: meta } = useGetDatasetFiltersQuery({ dataset, admin: isAdmin, variant });
  const { data, isLoading, isFetching, refetch } = useGetDatasetItemsQuery({
    dataset, admin: isAdmin, variant, ...filters, cursor,
  });

  const rows = data?.data ?? [];
  const info = data?.meta ?? meta?.meta;
  const states = meta?.states ?? [];
  const zones = meta?.zones ?? [];
  const units = meta?.units ?? [];
  const categories = meta?.categories ?? [];
  const years = meta?.years ?? [];
  const months = meta?.months ?? [];
  const measurements = meta?.measurements ?? [];

  /**
   * The states offered in the FILTER — only those the register has figures for.
   *
   * Two lists deliberately: the form below still picks from every state,
   * because the first entry for a chapter has to be creatable, while filtering
   * by a state the register holds nothing for can only ever return nothing.
   *
   * An empty `states_held` means the API does not know yet — a stats row older
   * than the column, or a register nobody has run `databank:refresh-stats` for
   * — and that falls back to the full list rather than to an empty dropdown.
   */
  const filterStates = useMemo(() => {
    const held = meta?.states_held ?? [];
    if (!held.length) return states;
    const inRegister = new Set(held);
    return states.filter((s) => inRegister.has(s.id));
  }, [states, meta?.states_held]);

  const depth = info?.taxonomy_depth ?? 2;
  // Labour outputs are not dated — a mason lays what a mason lays — so the
  // register says whether a year means anything here, and nothing about years
  // is rendered when it does not.
  const hasYear = info?.has_year ?? true;
  // The basic price list prices by month and carries a size/spec beside the
  // unit of sale; the rate registers do neither.
  const hasMonth = info?.has_month ?? false;
  const hasMeasurement = info?.has_measurement ?? false;
  const hasState = info?.has_state ?? true;
  // Infrastructure benchmarks are published per geopolitical zone (and
  // nationwide). A SECOND location beside the state, not a replacement for it:
  // an entry may carry a zone, a state, or both, so both are offered and
  // neither is required. The zone is free text and the options are the zones
  // the register holds, so the dropdown is empty until something is imported —
  // which is why the form offers a text box beside it.
  const hasZone = info?.has_zone ?? false;
  const hasCode = info?.has_code ?? false;
  // Most registers carry one number. Constants carries four, and the table and
  // the form are built from this list rather than from a single hard-coded one.
  const valueSpecs: DatasetValueSpec[] = useMemo(
    () => info?.values ?? [{ column: "value", label: info?.value_label ?? "Value", kind: info?.value_kind ?? "currency" }],
    [info?.values, info?.value_label, info?.value_kind],
  );
  const descriptionLabel = info?.description_label ?? "Description";
  const valueLabel = info?.value_label ?? "Value";
  // The variant actually in force, for writes — the API resolves the default
  // when we send none, and tells us which it picked.
  const activeVariant = info?.variant?.active ?? variant;

  const childrenOf = (list: DatasetCategory[], id: string) =>
    (list.find((c) => String(c.id) === id)?.children ?? []) as DatasetCategory[];

  const subCats = useMemo(
    () => childrenOf(categories, filters.category_id),
    [categories, filters.category_id],
  );
  const subCats2 = useMemo(
    () => childrenOf(subCats, filters.sub_category_id),
    [subCats, filters.sub_category_id],
  );

  /** Formats one of the register's numbers the way that number reads. */
  const fmt = (v: any, kind: DatasetValueSpec["kind"] = "currency") => {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    if (kind === "ratio") return n.toFixed(3);
    if (kind === "quantity") return n.toLocaleString("en-NG", { maximumFractionDigits: 4 });
    return `₦${n.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ── Admin actions ────────────────────────────────────────────────────────
  const [createEntry, { isLoading: creating }] = useCreateDatasetEntryMutation();
  const [updateEntry, { isLoading: updating }] = useUpdateDatasetEntryMutation();
  const [deleteEntry] = useDeleteDatasetEntryMutation();
  const [importFile, { isLoading: importing }] = useImportDatasetMutation();
  const [createCategory] = useCreateDatasetCategoryMutation();
  const [renameCategory] = useRenameDatasetCategoryMutation();
  const [deleteCategory] = useDeleteDatasetCategoryMutation();

  const [showForm, setShowForm] = useState(false);
  const [showCats, setShowCats] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [valueDraft, setValueDraft] = useState<ValueDraft>({});
  const [formError, setFormError] = useState("");
  const [banner, setBanner] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DatasetRow | null>(null);

  const setField = (k: keyof typeof emptyForm) => (e: any) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const formSubs = useMemo(
    () => childrenOf(categories, form.category_id),
    [categories, form.category_id],
  );
  const formSubs2 = useMemo(
    () => childrenOf(formSubs, form.sub_category_id),
    [formSubs, form.sub_category_id],
  );

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyForm });
    setValueDraft({});
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (r: DatasetRow) => {
    setEditId(r.id);
    setForm({
      state_id: r.state_id ? String(r.state_id) : "",
      zone: r.zone ?? "",
      description: r.description ?? "",
      unit: r.unit ?? "",
      measurement: r.measurement ?? "",
      code: r.code ?? "",
      category_id: r.category_id ? String(r.category_id) : "",
      sub_category_id: r.sub_category_id ? String(r.sub_category_id) : "",
      sub_category_2_id: r.sub_category_2_id ? String(r.sub_category_2_id) : "",
      year: r.year ? String(r.year) : "",
      month: r.month ? String(r.month) : "",
    });
    setValueDraft(
      Object.fromEntries(
        valueSpecs.map((v) => [v.column, r.values?.[v.column] == null ? "" : String(r.values[v.column])]),
      ),
    );
    setFormError(""); setShowForm(true);
  };

  const submit = async () => {
    setFormError("");

    const filled = valueSpecs.filter((v) => (valueDraft[v.column] ?? "") !== "");

    if (!form.description.trim() || !form.category_id || filled.length === 0) {
      setFormError(
        valueSpecs.length === 1
          ? `${descriptionLabel}, category and ${valueLabel.toLowerCase()} are required.`
          : `${descriptionLabel}, category and at least one of ${valueSpecs.map((v) => v.label).join(", ")} are required.`,
      );
      return;
    }

    const body: any = {
      description: form.description.trim(),
      unit: form.unit.trim() || null,
      category_id: Number(form.category_id),
      values: Object.fromEntries(
        valueSpecs.map((v) => [
          v.column,
          (valueDraft[v.column] ?? "") === "" ? null : Number(valueDraft[v.column]),
        ]),
      ),
    };
    if (hasState) body.state_id = form.state_id ? Number(form.state_id) : null;
    if (hasZone) body.zone = form.zone.trim() || null;
    if (hasCode) body.code = form.code.trim() || null;
    if (hasYear) body.year = form.year ? Number(form.year) : null;
    if (hasMonth) body.month = form.month ? Number(form.month) : null;
    if (hasMeasurement) body.measurement = form.measurement.trim() || null;
    // Levels the register does not file at are not sent at all — the API
    // rejects a sub-category on a register that files one level deep, and it is
    // right to.
    if (depth >= 2) body.sub_category_id = form.sub_category_id ? Number(form.sub_category_id) : null;
    if (depth >= 3) body.sub_category_2_id = form.sub_category_2_id ? Number(form.sub_category_2_id) : null;

    try {
      if (editId) await updateEntry({ dataset, variant: activeVariant, id: editId, body }).unwrap();
      else await createEntry({ dataset, variant: activeVariant, body }).unwrap();
      setBanner({ type: "success", text: editId ? "Entry updated." : "Entry added." });
      setShowForm(false);
    } catch (err: any) {
      // 409 is the register's natural key refusing a second rate for the same
      // item, unit, state and year. It is a normal thing to hit, not a fault,
      // so it is worded as a fact about the register rather than an error.
      setFormError(extractError(err));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteEntry({ dataset, variant: activeVariant, id: deleteTarget.id }).unwrap();
      setBanner({ type: "success", text: "Entry deleted." });
    } catch (err) {
      setBanner({ type: "error", text: extractError(err) });
    }
    setDeleteTarget(null);
  };

  const onImport = async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res: any = await importFile({ dataset, variant: activeVariant, file: fd }).unwrap();
      const unresolved = res?.unresolved_locations ?? [];
      // What did NOT go in, named. On a schedule form these are the items the
      // chapter left unpriced, and a count alone tells nobody which ones.
      const left: string[] = res?.errors ?? [];
      setBanner({
        type: "success",
        text: (res?.message || "Imported.") +
          (unresolved.length ? ` Unmatched locations: ${unresolved.slice(0, 5).join(", ")}.` : "") +
          (left.length
            ? ` Not written: ${left.slice(0, 3).join("; ")}${left.length > 3 ? ` and ${left.length - 3} more` : ""}.`
            : ""),
      });
    } catch (err) {
      setBanner({ type: "error", text: extractError(err) });
    }
  };

  /**
   * The columns THIS register's import reads, in the order the Institute's own
   * price sheets are written: location, description, unit, month, year, price,
   * then the filing — category and sub category last.
   *
   * That order is a convenience, not a rule: the importer reads the HEADER ROW,
   * so a file in any order still goes in. It is written this way so a secretariat
   * holding an existing spreadsheet can see at a glance that it already matches,
   * rather than reordering columns it did not need to touch.
   *
   * A register that records a SIZE beside the unit gets a `measurement` column
   * here; the basic price list no longer does, because its sheets write the
   * size into the item's name. That is decided by the register (has_measurement
   * in config/databank.php), not here, so this list cannot offer a column the
   * screen will not show.
   *
   * Built from the register's own meta rather than written out per register,
   * so it cannot drift from what the screen renders — and every name here is
   * one the importer's header map recognises (DatabankImporter::canonicalHeader).
   * Common synonyms work too (item/desc, area/state, size/spec, date/period),
   * which is why the note below says these are only the canonical spellings.
   */
  const importColumns = useMemo(() => {
    const cols: string[] = [];
    if (hasState) cols.push("location");
    if (hasZone) cols.push("zone");
    cols.push("description");
    if (hasCode) cols.push("code");
    if (hasMeasurement) cols.push("measurement");
    cols.push("unit");
    if (hasMonth) cols.push("month");
    if (hasYear) cols.push("year");
    valueSpecs.forEach((v) => cols.push(v.column));
    // Spelled the way the Institute's sheets spell them — "sub category", two
    // words — rather than in the underscored form the API uses internally. The
    // importer turns any run of spaces or dashes in a heading into an
    // underscore before it looks the column up, so both spellings read; this is
    // the one somebody can match against the spreadsheet already on their desk.
    cols.push("category");
    if (depth >= 2) cols.push("sub category");
    if (depth >= 3) cols.push("second level sub category");
    return cols;
  }, [hasState, hasZone, depth, hasCode, hasMeasurement, hasMonth, hasYear, valueSpecs]);

  /**
   * A header-only CSV, written in the browser.
   *
   * Header ONLY, with no example row: a sample line is the one thing certain to
   * be left in and imported as a real entry. Generated client-side rather than
   * fetched, so there is no endpoint to deploy and the file always matches the
   * register the screen is showing.
   */
  const downloadTemplate = () => {
    saveCsv(`${dataset}${activeVariant ? `-${activeVariant}` : ""}-template.csv`, [
      importColumns.join(","),
    ]);
  };

  /**
   * The same register, laid out the way the Institute's schedules are written:
   * a category as a HEADING across the sheet, with its items under it.
   *
   * Not a second format the API has to understand — the importer reads a row
   * with one cell filled as a heading that applies to everything beneath it
   * until the next, so this file and the flat one arrive at the same rows. It
   * exists because a secretariat typing up a printed schedule should not have
   * to repeat the category on all forty lines.
   *
   * The categories are written out IN FULL, so the download is the skeleton of
   * the schedule and the work is only to fill in the machines. Unlike a sample
   * data row — which this template still refuses to include — a heading left
   * unfilled imports nothing at all, so there is nothing here that can become
   * an entry nobody meant to create.
   */
  const downloadCategoryTemplate = () => {
    const lines = [importColumns.join(",")];
    const names = categories.length ? categories.map((c) => c.name) : ["Your first category"];

    names.forEach((name) => {
      lines.push(csvCell(name));
      lines.push("");                 // room for this category's items
    });

    saveCsv(`${dataset}${activeVariant ? `-${activeVariant}` : ""}-template-by-category.csv`, lines);
  };

  const inputCls =
    "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition";

  const chips = useMemo(() => {
    const out: { key: keyof Filters; label: string }[] = [];
    if (filters.q) out.push({ key: "q", label: `“${filters.q}”` });
    if (hasZone && filters.zone) out.push({ key: "zone", label: filters.zone });
    const st = hasState ? states.find((s) => String(s.id) === filters.state_id) : undefined;
    if (st) out.push({ key: "state_id", label: st.name });
    const cat = categories.find((c) => String(c.id) === filters.category_id);
    if (cat) out.push({ key: "category_id", label: cat.name });
    const sub = subCats.find((s) => String(s.id) === filters.sub_category_id);
    if (sub) out.push({ key: "sub_category_id", label: sub.name });
    const sub2 = subCats2.find((s) => String(s.id) === filters.sub_category_2_id);
    if (sub2) out.push({ key: "sub_category_2_id", label: sub2.name });
    const un = units.find((u) => String(u.id) === filters.unit_id);
    if (un) out.push({ key: "unit_id", label: un.name });
    const ms = measurements.find((m) => String(m.id) === filters.measurement_id);
    if (ms) out.push({ key: "measurement_id", label: ms.name });
    if (hasYear && filters.year) out.push({ key: "year", label: String(filters.year) });
    const mo = months.find((m) => String(m.id) === filters.month);
    if (mo) out.push({ key: "month", label: mo.name });
    return out;
  }, [filters, states, categories, subCats, subCats2, units, measurements, months, hasYear, hasState, hasZone]);

  const columnCount =
    3 + valueSpecs.length +
    (hasState ? 1 : 0) + (hasZone ? 1 : 0) + (hasCode ? 1 : 0) + (hasYear ? 1 : 0) + (hasMonth ? 1 : 0) +
    (hasMeasurement ? 1 : 0) + (depth >= 3 ? 1 : 0) + (isAdmin ? 1 : 0);

  return (
    <div className={embedded ? "space-y-5" : "max-w-7xl mx-auto p-4 md:p-6 space-y-5"}>
      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-600 to-blue-700 text-white p-6 md:p-8 shadow-lg shadow-indigo-200/50">
        <div className="absolute -top-16 -right-10 w-56 h-56 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <div className="p-3 rounded-2xl bg-white/15 backdrop-blur border border-white/20 shrink-0">
              <Database className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-black tracking-tight break-words">
                {info?.label ?? "Register"}
              </h1>
              <p className="text-sm text-indigo-100/90 mt-0.5">
                {info?.total_rows
                  ? `${info.total_rows.toLocaleString()} entries on the register`
                  : "Reference data by state and year."}
                {info?.refreshed_at && (
                  <span className="text-indigo-200/70">
                    {" "}· count as of {new Date(info.refreshed_at).toLocaleDateString()}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <>
                <button
                  onClick={() => setShowCats(true)}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 border border-white/20 text-sm font-semibold transition"
                >
                  <Tags className="w-4 h-4" /> Categories
                </button>
                <label className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 border border-white/20 text-sm font-semibold transition cursor-pointer">
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import
                  <input
                    type="file"
                    accept=".csv,.txt,.xlsx"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onImport(f);
                      e.target.value = "";
                    }}
                  />
                </label>
                <button
                  onClick={openCreate}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white text-indigo-700 hover:bg-indigo-50 text-sm font-bold transition"
                >
                  <Plus className="w-4 h-4" /> Add entry
                </button>
              </>
            )}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 border border-white/20 text-sm font-semibold transition disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Variant tabs — only for a register that has them. An upload and an
            "Add entry" go into whichever tab is showing, which is why the tab
            sits here beside those buttons rather than down among the filters. */}
        {info?.variant && (
          <div className="relative mt-6 inline-flex items-center gap-1 p-1 rounded-xl bg-white/10 border border-white/20">
            {info.variant.options.map((o) => {
              const on = (activeVariant ?? info.variant!.active) === o.key;
              return (
                <button
                  key={o.key}
                  onClick={() => setVariant(o.key)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-bold transition ${
                    on ? "bg-white text-indigo-700" : "text-indigo-100 hover:bg-white/10"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Import format ───────────────────────────────────────────────────
          Admin only, and under the Import button rather than inside it: the
          register that was uploaded into with the wrong columns is the one
          nobody was told what the columns were. The legacy price screen printed
          its header on the page and this one did not, which is the whole
          reason an upload here used to be guesswork. */}
      {isAdmin && (
        <div className="bg-white border border-gray-100 rounded-2xl p-4 md:p-5 shadow-sm space-y-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-[11px] font-black uppercase tracking-widest text-gray-400">
              CSV / Excel columns
              {info?.variant && (
                <span className="text-indigo-500">
                  {" "}· {info.variant.options.find((o) => o.key === activeVariant)?.label}
                </span>
              )}
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={downloadTemplate}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-xs font-semibold text-gray-600 transition"
              >
                <FileDown className="w-3.5 h-3.5" /> Columns
              </button>
              <button
                onClick={downloadCategoryTemplate}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-xs font-semibold text-gray-600 transition"
              >
                <FileDown className="w-3.5 h-3.5" /> By category
              </button>
            </div>
          </div>

          <p className="font-mono text-xs text-gray-700 break-words">{importColumns.join(",")}</p>

          <p className="text-[11px] text-gray-400">
            First row is the header; order does not matter and the usual synonyms are read
            (<span className="font-mono">item</span>, <span className="font-mono">desc</span>,{" "}
            <span className="font-mono">size</span>, <span className="font-mono">area</span>,{" "}
            <span className="font-mono">date</span>). A row with no{" "}
            {descriptionLabel.toLowerCase()} is skipped.
            {/* The register's number is the column most likely to be spelled
                differently from this list: the sheets in circulation head it
                "price" whatever the register calls it, because that is what the
                old databank called it on every register. */}
            {valueSpecs.length === 1 && valueSpecs[0].column !== "price" && (
              <>
                {" "}A column headed <span className="font-mono">price</span>,{" "}
                <span className="font-mono">amount</span> or{" "}
                <span className="font-mono">value</span> is read as{" "}
                <span className="font-mono">{valueSpecs[0].column}</span>, so a sheet written for
                the old databank needs no re-heading.
              </>
            )}
            {hasMeasurement && (
              <>
                {" "}<span className="font-mono">measurement</span> is the SIZE (12mm, 50kg), not
                the unit it is sold by — leave it empty where the size is already part of the{" "}
                {descriptionLabel.toLowerCase()}.
              </>
            )}
            {hasState && (
              <>
                {" "}<span className="font-semibold">location</span> must be a state name — anything
                unrecognised is reported back and the row goes in without a state.
              </>
            )}
            {" "}The Institute's own <span className="font-semibold">schedule form</span> uploads as
            it is — the one with the state and the date in the margin above the header and the
            categories as lettered rows (<span className="font-mono">A Heavy Equipment…</span>).
            It is recognised on sight, its state and date are read from the margin and echoed
            back, and an item the form leaves unpriced is listed rather than written as a blank
            entry.
            {" "}A row with only ONE cell filled is read as a heading, not an entry: it names the
            category{hasState && " (or the state)"} for every row beneath it until the next
            heading — which is how the Institute's printed schedules are laid out, and what the{" "}
            <span className="font-semibold">By category</span> template gives you. A row that
            names its own category overrides the heading, so the two can be mixed.
            {" "}Categories and sub-categories are created as they are met. Re-importing a corrected
            file updates the matching entries rather than duplicating them, but changing the{" "}
            {[hasState && "state", hasMeasurement && "measurement", "unit", hasYear && "period"]
              .filter(Boolean)
              .join(", ")}{" "}
            makes a new entry instead.
            {" "}Two lines of the SAME file that match on those become one entry, the last one
            winning, and the upload says how many — which is worth reading on a sheet written
            with <span className="font-mono">ditto</span> {descriptionLabel.toLowerCase()}s, since
            a repeated “ditto” no longer has the line above it to mean anything by.
            {" "}.csv, .txt or .xlsx, up to 20MB; only the first worksheet of a workbook is read.
            {info?.variant && (
              <>
                {" "}The upload lands in the{" "}
                <span className="font-semibold">
                  {info.variant.options.find((o) => o.key === activeVariant)?.label}
                </span>{" "}
                tab shown above — switch it first if that is not where these rows belong.
              </>
            )}
          </p>
        </div>
      )}

      {banner && (
        <div
          className={`rounded-xl px-4 py-3 text-sm flex items-start justify-between gap-3 ${
            banner.type === "success"
              ? "bg-emerald-50 border border-emerald-100 text-emerald-800"
              : "bg-rose-50 border border-rose-100 text-rose-800"
          }`}
        >
          <span>{banner.text}</span>
          <button onClick={() => setBanner(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 md:p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3 text-gray-400">
          <SlidersHorizontal className="w-4 h-4" />
          <span className="text-[11px] font-black uppercase tracking-widest">Filter</span>
        </div>

        {/* One filter per row on a phone: these are selects with long option
            text (state names, category paths), and two to a row truncates both. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-3">
          <div className="sm:col-span-2 md:col-span-4 relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && patch({ q: searchDraft })}
              onBlur={() => searchDraft !== filters.q && patch({ q: searchDraft })}
              placeholder={`Search ${descriptionLabel.toLowerCase()}… (press Enter)`}
              className={`${inputCls} pl-9`}
            />
          </div>

          {hasZone && zones.length > 0 && (
            <div className="md:col-span-2">
              <select value={filters.zone} onChange={(e) => patch({ zone: e.target.value })} className={inputCls}>
                <option value="">All zones</option>
                {zones.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
          )}

          {hasState && (
            <div className="md:col-span-2">
              <select value={filters.state_id} onChange={(e) => patch({ state_id: e.target.value })} className={inputCls}>
                <option value="">All states</option>
                {filterStates.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              </select>
            </div>
          )}

          <div className="md:col-span-2">
            <select
              value={filters.category_id}
              onChange={(e) => patch({ category_id: e.target.value, sub_category_id: "", sub_category_2_id: "" })}
              className={inputCls}
            >
              <option value="">All categories</option>
              {categories.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          </div>

          {depth >= 2 && (
            <div className="md:col-span-2">
              <select
                value={filters.sub_category_id}
                onChange={(e) => patch({ sub_category_id: e.target.value, sub_category_2_id: "" })}
                className={inputCls}
                disabled={!filters.category_id}
              >
                <option value="">All sub-categories</option>
                {subCats.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              </select>
            </div>
          )}

          {depth >= 3 && (
            <div className="md:col-span-2">
              <select
                value={filters.sub_category_2_id}
                onChange={(e) => patch({ sub_category_2_id: e.target.value })}
                className={inputCls}
                disabled={!filters.sub_category_id}
              >
                <option value="">All second-level</option>
                {subCats2.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              </select>
            </div>
          )}

          <div className="md:col-span-1">
            <select value={filters.unit_id} onChange={(e) => patch({ unit_id: e.target.value })} className={inputCls}>
              <option value="">Unit</option>
              {units.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
            </select>
          </div>

          {hasMeasurement && (
            <div className="md:col-span-2">
              <select
                value={filters.measurement_id}
                onChange={(e) => patch({ measurement_id: e.target.value })}
                className={inputCls}
              >
                <option value="">All sizes</option>
                {measurements.map((m) => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
              </select>
            </div>
          )}

          {hasYear && (
            <div className="md:col-span-1">
              <select value={filters.year} onChange={(e) => patch({ year: e.target.value })} className={inputCls}>
                <option value="">Year</option>
                {years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
              </select>
            </div>
          )}

          {hasMonth && (
            <div className="md:col-span-2">
              <select value={filters.month} onChange={(e) => patch({ month: e.target.value })} className={inputCls}>
                <option value="">All months</option>
                {months.map((m) => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {chips.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mt-3">
            {chips.map((c) => (
              <button
                key={c.key}
                onClick={() => {
                  if (c.key === "q") setSearchDraft("");
                  patch({ [c.key]: "" } as Partial<Filters>);
                }}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full px-2.5 py-1"
              >
                {c.label} <X className="w-3 h-3" />
              </button>
            ))}
            <button
              onClick={() => { setSearchDraft(""); setFilters({ ...EMPTY }); setCursorStack([null]); }}
              className="text-[11px] font-semibold text-gray-500 hover:text-gray-700 underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* ── The register ─────────────────────────────────────────────────
          Two renderings of the same rows. A register carries up to a dozen
          columns — zone, state, code, description, size, unit, category, two
          sub-levels, month, year, then the rates themselves — and no amount of
          horizontal scrolling makes that readable on a phone. So below `md`
          each row becomes a card: the description leads, the rates are stated
          plainly, and everything else is a labelled pair. */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        {/* Phone and tablet, up to a full-width desktop */}
        <div className="lg:hidden divide-y divide-gray-100">
          {isLoading && (
            <div className="px-4 py-16 text-center text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            </div>
          )}

          {!isLoading && rows.length === 0 && (
            <div className="px-4 py-16 text-center text-gray-400 text-sm">
              Nothing on the register for this selection.
            </div>
          )}

          {rows.map((r) => (
            <div key={r.id} className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 leading-snug">
                    {r.description ?? "—"}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap mt-1 text-[11px] text-gray-400">
                    {hasCode && r.code && <span className="font-mono">{r.code}</span>}
                    {r.category && <span>{r.category}</span>}
                    {r.sub_category && <span>· {r.sub_category}</span>}
                    {depth >= 3 && r.sub_category_2 && <span>· {r.sub_category_2}</span>}
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleteTarget(r)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* The figures — the reason anybody opened the register. */}
              <div className="grid grid-cols-2 gap-2">
                {valueSpecs.map((v) => (
                  <div key={v.column} className="rounded-lg bg-gray-50 px-3 py-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      {v.label}
                    </div>
                    <div className="text-sm font-bold text-gray-900">
                      {fmt(r.values?.[v.column], v.kind)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Everything that qualifies the figure above. */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500">
                {r.unit && <span><span className="text-gray-400">Unit</span> {r.unit}</span>}
                {hasMeasurement && r.measurement && (
                  <span><span className="text-gray-400">Size</span> {r.measurement}</span>
                )}
                {hasZone && r.zone && <span><span className="text-gray-400">Zone</span> {r.zone}</span>}
                {hasState && r.state && <span><span className="text-gray-400">State</span> {r.state}</span>}
                {hasMonth && r.month_name && (
                  <span><span className="text-gray-400">Month</span> {r.month_name}</span>
                )}
                {hasYear && r.year && <span><span className="text-gray-400">Year</span> {r.year}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop.
            NOTHING SCROLLS SIDEWAYS. Two things make that true:

            · the secondary columns — zone, state, size, second level, month,
              year — drop out below `xl`, so there are fewer of them to fit;
            · every remaining cell WRAPS. A long description or a two-word
              category takes a second line inside its own column instead of
              pushing the table wider than the screen.

            `table-fixed` is what makes the wrapping hold: without it the
            browser widens columns to fit their longest word and the table
            overflows again however much text is allowed to wrap. */}
        <div className="hidden lg:block">
          <table className="w-full table-fixed text-sm break-words">
            <thead className="bg-gray-50 text-gray-500">
              <tr className="text-left">
                {/* Widths live on the header row because `table-fixed` reads
                    them from the first row. The description is left unsized so
                    it takes whatever the others do not — it is the column with
                    real text in it, and the one that should get the slack. */}
                {hasZone && (
                  <th className="hidden xl:table-cell w-24 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Zone</th>
                )}
                {hasState && (
                  <th className="hidden xl:table-cell w-28 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">State</th>
                )}
                {hasCode && (
                  <th className="w-28 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Code</th>
                )}
                <th className="px-4 py-3 font-bold text-[11px] uppercase tracking-wider">{descriptionLabel}</th>
                {hasMeasurement && (
                  <th className="hidden xl:table-cell w-24 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Size</th>
                )}
                <th className="w-20 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Unit</th>
                {/* Month, year, then the filing — the order the register's own
                    sheets are written in. The figures stay at the far right
                    whatever the sheet does, because they are right-aligned and
                    they are what the eye comes down the table looking for. */}
                {hasMonth && (
                  <th className="hidden xl:table-cell w-24 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Month</th>
                )}
                {hasYear && (
                  <th className="hidden xl:table-cell w-16 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Year</th>
                )}
                <th className="w-40 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Category</th>
                {depth >= 3 && (
                  <th className="hidden xl:table-cell w-32 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Second level</th>
                )}
                {valueSpecs.map((v) => (
                  <th key={v.column} className="w-32 px-4 py-3 font-bold text-[11px] uppercase tracking-wider text-right">
                    {v.label}
                  </th>
                ))}
                {isAdmin && <th className="w-24 px-4 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-16 text-center text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  </td>
                </tr>
              )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-16 text-center text-gray-400">
                    Nothing on the register for this selection.
                  </td>
                </tr>
              )}

              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50/60">
                  {hasZone && (
                    <td className="hidden xl:table-cell px-4 py-3 align-top text-gray-700">{r.zone ?? "—"}</td>
                  )}
                  {hasState && (
                    <td className="hidden xl:table-cell px-4 py-3 align-top text-gray-700">{r.state ?? "—"}</td>
                  )}
                  {hasCode && (
                    <td className="px-4 py-3 align-top text-gray-500 font-mono text-xs">{r.code ?? "—"}</td>
                  )}
                  <td className="px-4 py-3 align-top text-gray-900">{r.description ?? "—"}</td>
                  {hasMeasurement && (
                    <td className="hidden xl:table-cell px-4 py-3 align-top text-gray-500">{r.measurement ?? "—"}</td>
                  )}
                  <td className="px-4 py-3 align-top text-gray-500">{r.unit ?? "—"}</td>
                  {hasMonth && (
                    <td className="hidden xl:table-cell px-4 py-3 align-top text-gray-500">{r.month_name ?? "—"}</td>
                  )}
                  {hasYear && <td className="hidden xl:table-cell px-4 py-3 align-top text-gray-500">{r.year ?? "—"}</td>}
                  <td className="px-4 py-3 align-top text-gray-500">
                    {r.category ?? "—"}
                    {r.sub_category && <span className="text-gray-400"> · {r.sub_category}</span>}
                  </td>
                  {depth >= 3 && (
                    <td className="hidden xl:table-cell px-4 py-3 align-top text-gray-500">{r.sub_category_2 ?? "—"}</td>
                  )}
                  {valueSpecs.map((v) => (
                    <td key={v.column} className="px-4 py-3 align-top text-right font-semibold text-gray-900">
                      {fmt(r.values?.[v.column], v.kind)}
                    </td>
                  ))}
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteTarget(r)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cursor pager — no page numbers, by design (see the file header). */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
          <span className="text-xs text-gray-500">
            {rows.length > 0 ? `${rows.length} entries on this page` : ""}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCursorStack((s) => (s.length > 1 ? s.slice(0, -1) : s))}
              disabled={cursorStack.length <= 1 || isFetching}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <button
              onClick={() => data?.next_cursor && setCursorStack((s) => [...s, data.next_cursor])}
              disabled={!data?.has_more || isFetching}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm disabled:opacity-40"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Add / edit ──────────────────────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-semibold text-gray-900">
                {editId ? "Edit entry" : `Add to ${info?.label ?? "register"}`}
                {info?.variant && (
                  <span className="text-gray-400 font-normal">
                    {" "}· {info.variant.options.find((o) => o.key === activeVariant)?.label}
                  </span>
                )}
              </h3>
              <button onClick={() => setShowForm(false)}><X className="w-4 h-4 text-gray-500" /></button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              {formError && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                  {formError}
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-gray-600">{descriptionLabel}</label>
                <textarea
                  value={form.description}
                  onChange={setField("description")}
                  rows={2}
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {hasZone && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Zone</label>
                    {/* A list to pick from AND a box to type in: the options are
                        the zones already on the register, so picking keeps the
                        spelling consistent, and typing is what lets the first
                        entry for a new zone exist at all. */}
                    <input
                      value={form.zone}
                      onChange={setField("zone")}
                      list="databank-zones"
                      placeholder="e.g. North-West"
                      className={inputCls}
                    />
                    <datalist id="databank-zones">
                      {zones.map((z) => <option key={z} value={z} />)}
                    </datalist>
                  </div>
                )}

                {hasState && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">State</label>
                    <select value={form.state_id} onChange={setField("state_id")} className={inputCls}>
                      <option value="">—</option>
                      {states.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                    </select>
                  </div>
                )}

                {hasCode && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Code</label>
                    <input value={form.code} onChange={setField("code")} placeholder="e.g. C1.02" className={inputCls} />
                  </div>
                )}
                <div>
                  <label className="text-xs font-semibold text-gray-600">Unit</label>
                  <input
                    value={form.unit}
                    onChange={setField("unit")}
                    list="dataset-units"
                    placeholder="M3, Bag, Day…"
                    className={inputCls}
                  />
                  <datalist id="dataset-units">
                    {units.map((u) => <option key={u.id} value={u.name} />)}
                  </datalist>
                </div>
                {hasMeasurement && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Size / spec</label>
                    <input
                      value={form.measurement}
                      onChange={setField("measurement")}
                      list="dataset-measurements"
                      placeholder="50kg, 12mm…"
                      className={inputCls}
                    />
                    <datalist id="dataset-measurements">
                      {measurements.map((m) => <option key={m.id} value={m.name} />)}
                    </datalist>
                  </div>
                )}

                {hasYear && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Year</label>
                    <input value={form.year} onChange={setField("year")} inputMode="numeric" className={inputCls} />
                  </div>
                )}

                {hasMonth && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Month</label>
                    <select value={form.month} onChange={setField("month")} className={inputCls}>
                      <option value="">—</option>
                      {months.map((m) => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-xs font-semibold text-gray-600">Category</label>
                  <select
                    value={form.category_id}
                    onChange={(e) => setForm((p) => ({
                      ...p, category_id: e.target.value, sub_category_id: "", sub_category_2_id: "",
                    }))}
                    className={inputCls}
                  >
                    <option value="">—</option>
                    {categories.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                  </select>
                </div>

                {depth >= 2 && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Sub-category</label>
                    <select
                      value={form.sub_category_id}
                      onChange={(e) => setForm((p) => ({
                        ...p, sub_category_id: e.target.value, sub_category_2_id: "",
                      }))}
                      className={inputCls}
                      disabled={!form.category_id}
                    >
                      <option value="">—</option>
                      {formSubs.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                    </select>
                  </div>
                )}

                {depth >= 3 && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Second-level sub-category</label>
                    <select
                      value={form.sub_category_2_id}
                      onChange={setField("sub_category_2_id")}
                      className={inputCls}
                      disabled={!form.sub_category_id}
                    >
                      <option value="">—</option>
                      {formSubs2.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                    </select>
                  </div>
                )}

                {valueSpecs.map((v) => (
                  <div key={v.column}>
                    <label className="text-xs font-semibold text-gray-600">{v.label}</label>
                    <input
                      value={valueDraft[v.column] ?? ""}
                      onChange={(e) => setValueDraft((p) => ({ ...p, [v.column]: e.target.value }))}
                      inputMode="decimal"
                      className={inputCls}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg border text-sm">
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={creating || updating}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-60"
              >
                {(creating || updating) && <Loader2 className="w-4 h-4 animate-spin" />}
                {editId ? "Save changes" : "Add entry"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Categories ──────────────────────────────────────────────────── */}
      {showCats && (
        <CategoriesPanel
          title={`Categories — ${info?.label ?? dataset}${
            info?.variant ? ` · ${info.variant.options.find((o) => o.key === activeVariant)?.label}` : ""
          }`}
          categories={categories}
          maxDepth={depth}
          onClose={() => setShowCats(false)}
          onCreate={(name, parent_id) =>
            createCategory({ dataset, variant: activeVariant, name, parent_id }).unwrap()}
          onRename={(id, name, level) =>
            renameCategory({ dataset, variant: activeVariant, id, name, level }).unwrap()}
          onDelete={(id, level) =>
            deleteCategory({ dataset, variant: activeVariant, id, level }).unwrap()}
        />
      )}

      {/* ── Delete confirm ──────────────────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Delete this entry?</h3>
            <p className="text-sm text-gray-500">{deleteTarget.description}</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 rounded-lg border text-sm">
                Cancel
              </button>
              <button onClick={confirmDelete} className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

/** What each level of filing is CALLED, as far as this register files. */
const LEVEL_NAMES = ["Category", "Sub-category", "Second level"];

/**
 * The category tree as flat rows, each carrying the level it sits at.
 *
 * Depth-first, so a category is immediately followed by what is filed under
 * it — the order the list is read in, and the order the table prints.
 */
function flattenTaxonomy(
  nodes: DatasetCategory[],
  level = 0,
  out: Array<{ node: DatasetCategory; level: number }> = [],
) {
  nodes.forEach((n) => {
    out.push({ node: n, level });
    flattenTaxonomy(n.children ?? [], level + 1, out);
  });
  return out;
}

/**
 * Narrow the tree to what matches, keeping the branch above a match.
 *
 * A hit KEEPS ITS CHILDREN — searching "Earthwork" should show what is filed
 * under Earthwork, not just the heading — while a parent that misses is kept
 * only for the matching children beneath it, so the match is never shown
 * floating with no indication of where it is filed.
 */
function filterTaxonomy(nodes: DatasetCategory[], q: string): DatasetCategory[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return nodes;

  const walk = (list: DatasetCategory[]): DatasetCategory[] =>
    list.flatMap((n) => {
      const hit = n.name.toLowerCase().includes(needle);
      const kids = walk(n.children ?? []);
      if (hit) return [{ ...n, children: n.children ?? [] }];
      return kids.length ? [{ ...n, children: kids }] : [];
    });

  return walk(nodes);
}

/**
 * The register's categories, as a table.
 *
 * One row per category and per sub-category, each name sitting in the column
 * for its own level — so the panel reads the way the Institute's own sheets are
 * written (category, sub category) and every button acts on exactly the row it
 * sits beside. Recursive to whatever depth the register files at: plant hire
 * files one level, composite rates two, formal labour three, and an editor per
 * depth would be three editors that drift apart.
 *
 * NO count of entries beside a category, deliberately. That number is a
 * COUNT(*) on a fact table this module is built never to run — the same reason
 * the register pages by cursor — and the category filter on the register itself
 * answers "what is filed here" without one.
 *
 * Renaming goes through PUT, so the id survives and every entry already filed
 * under the category follows the new spelling. That is the common repair: an
 * import creates categories as it meets them, so one misspelt heading in a
 * spreadsheet becomes a category, and delete-and-recreate would orphan
 * everything filed under it.
 */
function CategoriesPanel({
  title, categories, maxDepth, onClose, onCreate, onRename, onDelete,
}: {
  title: string;
  categories: DatasetCategory[];
  maxDepth: number;
  onClose: () => void;
  onCreate: (name: string, parentId?: number) => Promise<any>;
  // The level rides along because one register keeps its sub-categories in
  // their own table, where an id alone does not say which row is meant.
  onRename: (id: number, name: string, level: CategoryLevel) => Promise<any>;
  onDelete: (id: number, level: CategoryLevel) => Promise<any>;
}) {
  const [name, setName] = useState("");
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [addingUnder, setAddingUnder] = useState<number | null>(null);
  const [childDraft, setChildDraft] = useState("");
  const [renaming, setRenaming] = useState<number | null>(null);
  const [renameDraft, setRenameDraft] = useState("");

  const levels = LEVEL_NAMES.slice(0, maxDepth);
  const rows = useMemo(
    () => flattenTaxonomy(filterTaxonomy(categories, query)),
    [categories, query],
  );

  // Every write goes through here so that one failure — a duplicate name, a
  // register that does not file that deep — lands in the same place on screen
  // instead of each button inventing its own error handling.
  const run = async (fn: () => Promise<any>, after?: () => void) => {
    setError("");
    setBusy(true);
    try {
      await fn();
      after?.();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const addRoot = () => {
    if (name.trim()) run(() => onCreate(name.trim()), () => setName(""));
  };

  const addChild = (parentId: number) => {
    if (!childDraft.trim()) return;
    run(() => onCreate(childDraft.trim(), parentId), () => {
      setChildDraft("");
      setAddingUnder(null);
    });
  };

  const levelOf = (level: number): CategoryLevel => (level === 0 ? "category" : "sub");

  const saveRename = (id: number, level: number) => {
    if (!renameDraft.trim()) return;
    run(() => onRename(id, renameDraft.trim(), levelOf(level)), () => setRenaming(null));
  };

  const remove = (node: DatasetCategory, level: number) => {
    // Only a top-level category on a register that files deeper takes anything
    // with it; promising that about a sub-category would read as a bigger
    // deletion than it is.
    const warning = level === 0 && maxDepth > 1
      ? `Delete "${node.name}" and everything filed under it?`
      : `Delete "${node.name}"?`;
    if (window.confirm(`${warning} Entries using it will show as unassigned.`)) {
      run(() => onDelete(node.id, levelOf(level)));
    }
  };

  const inputCls =
    "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm break-words">{title}</h3>
            <p className="text-[11px] text-gray-400 mt-0.5">
              {levels.join(" → ")} · the list the register files under and the upload fills in
            </p>
          </div>
          <button onClick={onClose} className="shrink-0 p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Add and find, above the table — the two things somebody opens this
            panel to do, and neither should need scrolling to reach. */}
        <div className="px-6 py-4 border-b bg-gray-50/60 space-y-3">
          <div className="flex gap-2 flex-wrap">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addRoot()}
              placeholder="New category…"
              className={`flex-1 min-w-[12rem] ${inputCls}`}
            />
            <button
              onClick={addRoot}
              disabled={busy || !name.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> Add category
            </button>
          </div>

          {categories.length > 8 && (
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find a category…"
                className={`${inputCls} pl-9`}
              />
            </div>
          )}

          {error && (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="overflow-y-auto">
          <table className="w-full table-fixed text-sm break-words">
            <thead className="bg-white border-b border-gray-100">
              <tr className="text-left text-gray-500">
                {levels.map((l, i) => (
                  <th key={l} className={`${i === 0 ? "px-6" : "px-4"} py-2.5 font-bold text-[11px] uppercase tracking-wider`}>
                    {l}
                  </th>
                ))}
                <th className="w-28 px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={maxDepth + 1} className="px-6 py-14 text-center text-sm text-gray-400">
                    {categories.length === 0
                      ? "No categories yet. Add the first one above, or let an upload create them as it meets them."
                      : "Nothing matches that."}
                  </td>
                </tr>
              )}

              {rows.map(({ node, level }) => (
                <Fragment key={node.id}>
                  <tr className="hover:bg-gray-50/60">
                    {levels.map((_, col) => (
                      <td key={col} className={`${col === 0 ? "px-6" : "px-4"} py-2.5 align-middle`}>
                        {col !== level ? null : renaming === node.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              autoFocus
                              value={renameDraft}
                              onChange={(e) => setRenameDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveRename(node.id, level);
                                if (e.key === "Escape") setRenaming(null);
                              }}
                              className="flex-1 min-w-0 border border-indigo-200 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                            />
                            <button
                              onClick={() => saveRename(node.id, level)}
                              disabled={busy}
                              className="px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setRenaming(null)}
                              className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <span className={level === 0 ? "font-semibold text-gray-800" : "text-gray-600"}>
                            {node.name}
                          </span>
                        )}
                      </td>
                    ))}

                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {/* A register that files two levels deep gets no "add
                            under this" at level two. */}
                        {level + 1 < maxDepth && (
                          <button
                            title={`Add a ${LEVEL_NAMES[level + 1].toLowerCase()} under this`}
                            onClick={() => {
                              setChildDraft("");
                              setAddingUnder(addingUnder === node.id ? null : node.id);
                            }}
                            className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-500"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          title="Rename"
                          onClick={() => { setRenameDraft(node.name); setRenaming(node.id); }}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          title="Delete"
                          onClick={() => remove(node, level)}
                          className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* The new sub-category types in the column it will live in,
                      so the row being written looks like the rows around it. */}
                  {addingUnder === node.id && (
                    <tr className="bg-indigo-50/40">
                      {levels.map((_, col) => (
                        <td key={col} className={`${col === 0 ? "px-6" : "px-4"} py-2`}>
                          {col === level + 1 && (
                            <input
                              autoFocus
                              value={childDraft}
                              onChange={(e) => setChildDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") addChild(node.id);
                                if (e.key === "Escape") setAddingUnder(null);
                              }}
                              placeholder={`New ${LEVEL_NAMES[level + 1].toLowerCase()} under ${node.name}…`}
                              className="w-full border border-indigo-200 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                            />
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => addChild(node.id)}
                            disabled={busy || !childDraft.trim()}
                            className="px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold disabled:opacity-50"
                          >
                            Add
                          </button>
                          <button
                            onClick={() => setAddingUnder(null)}
                            className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs"
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
